import React, { useContext, useState, useEffect } from 'react';
import { Eye, CheckSquare, Play, Check, X, Upload, Paperclip } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../../context/AuthContext';
import { useToast } from '../../../../context/ToastContext';
import taskService from '../../../../services/taskService';
import Pagination from '../../../../components/Pagination';

export default function MyAssignedTasks() {
  const { authTokens } = useContext(AuthContext);
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });

  const [confirmationModal, setConfirmationModal] = useState({ 
    isOpen: false, 
    taskId: null, 
    action: null,
    message: ''
  });

  const [completionComment, setCompletionComment] = useState('');
  
  // Attachment upload modal state
  const [attachmentModal, setAttachmentModal] = useState({
    isOpen: false,
    taskId: null,
    taskName: ''
  });
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchMyTasks();
  }, [pagination.page, pagination.page_size]);

  const fetchMyTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const pagination_data = {
        page: pagination.page,
        page_size: pagination.page_size,    
      };
      const result = await taskService.getMyTasks(authTokens.access, pagination_data);
      if (result.success) {
        setTasks(result.data.results);

        setPagination(prev => ({
          ...prev,
          count: result.data.count,
          total_pages: result.data.total_pages
        }));
      } else {
        setError(result.error);
        showError(result.error || 'Erreur lors du chargement de vos tâches');
      }
    } catch (err) {
      setError('Erreur de connexion');
      showError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      page_size: newPageSize, 
      page: 1
    }));
  };

  const handleViewDetails = (taskId) => {
    navigate(`/dashboard/tasks/${taskId}`);
  };

  const openConfirmationModal = (taskId, action, message) => {
    setConfirmationModal({ isOpen: true, taskId, action, message });
    // Reset comment when opening modal
    if (action === 'complete') {
      setCompletionComment('');
    }
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({ isOpen: false, taskId: null, action: null, message: '' });
    setCompletionComment('');
    setSelectedFiles([]);
  };

  const openAttachmentModal = (taskId, taskName) => {
    setAttachmentModal({ isOpen: true, taskId, taskName });
    setSelectedFiles([]);
  };

  const closeAttachmentModal = () => {
    setAttachmentModal({ isOpen: false, taskId: null, taskName: '' });
    setSelectedFiles([]);
    setUploadProgress({});
  };

  const handleFileSelection = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    
    for (const file of files) {
      if (!taskService.isValidFileType(file.name)) {
        showError(`Type de fichier non autorisé: ${file.name}`);
        continue;
      }
      
      if (!taskService.isValidFileSize(file.size)) {
        showError(`Fichier trop volumineux: ${file.name} (max 5MB)`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAttachmentUpload = async () => {
    if (selectedFiles.length === 0) {
      showError('Veuillez sélectionner au moins un fichier');
      return;
    }

    setIsUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', ''); // Optional description

      try {
        setUploadProgress(prev => ({ ...prev, [i]: 'uploading' }));
        
        const result = await taskService.uploadTaskAttachment(
          attachmentModal.taskId, 
          formData, 
          authTokens.access
        );

        if (result.success) {
          setUploadProgress(prev => ({ ...prev, [i]: 'success' }));
          successCount++;
        } else {
          setUploadProgress(prev => ({ ...prev, [i]: 'error' }));
          errorCount++;
          showError(`Erreur lors de l'upload de ${file.name}: ${result.error}`);
        }
      } catch (error) {
        setUploadProgress(prev => ({ ...prev, [i]: 'error' }));
        errorCount++;
        showError(`Erreur lors de l'upload de ${file.name}`);
      }
    }

    if (successCount > 0) {
      showSuccess(`${successCount} fichier(s) uploadé(s) avec succès`);
    }
    
    setIsUploading(false);
    
    // Close modal after a short delay to show results
    setTimeout(() => {
      closeAttachmentModal();
    }, 1500);
  };

  const handleConfirmAction = async () => {
    const { taskId, action } = confirmationModal;
    if (!taskId || !action) return;

    try {
      let result;
      const actionMessages = {
        'accept': 'Tâche acceptée avec succès !',
        'reject': 'Tâche rejetée',
        'start': 'Tâche démarrée !',
        'complete': 'Tâche terminée !'
      };

      // Handle file uploads for completion if files are selected
      if (action === 'complete' && selectedFiles.length > 0) {
        setIsUploading(true);
        
        // First complete the task
        result = await taskService.completeTask(taskId, authTokens.access, completionComment);
        
        if (result.success) {
          // Then upload attachments
          let uploadSuccessCount = 0;
          for (const file of selectedFiles) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('description', '');
            
            const uploadResult = await taskService.uploadTaskAttachment(taskId, formData, authTokens.access);
            if (uploadResult.success) {
              uploadSuccessCount++;
            }
          }
          
          if (uploadSuccessCount > 0) {
            showSuccess(`${actionMessages[action]} ${uploadSuccessCount} fichier(s) uploadé(s)`);
          } else {
            showSuccess(actionMessages[action]);
          }
        }
        
        setIsUploading(false);
      } else {
        // Handle normal actions without file upload
        switch (action) {
          case 'accept':
            result = await taskService.acceptTask(taskId, authTokens.access);
            break;
          case 'reject':
            result = await taskService.rejectTask(taskId, authTokens.access);
            break;
          case 'start':
            result = await taskService.startTask(taskId, authTokens.access);
            break;
          case 'complete':
            result = await taskService.completeTask(taskId, authTokens.access, completionComment);
            break;
          default:
            showError('Action non reconnue');
            return;
        }

        if (result.success) {
          showSuccess(actionMessages[action] || 'Action effectuée !');
        } else {
          showError(result.error || 'Erreur lors de l\'action');
        }
      }

      if (result.success) {
        fetchMyTasks();
      }
    } catch (err) {
      showError('Erreur de connexion');
    } finally {
      closeConfirmationModal();
      setIsUploading(false);
    }
  };

  const handleTaskAction = (task, action) => {
    const actionMessages = {
      'accept': 'Êtes-vous sûr de vouloir accepter cette tâche ?',
      'reject': 'Êtes-vous sûr de vouloir rejeter cette tâche ?',
      'start': 'Êtes-vous sûr de vouloir commencer cette tâche ?',
      'complete': 'Marquer cette tâche comme terminée'
    };
    
    openConfirmationModal(task.id, action, actionMessages[action]);
  };

  const getAvailableActions = (task) => {
    return taskService.getAvailableActions(task.status);
  };

  const getActionButtons = (task) => {
    const availableActions = getAvailableActions(task);
    const actionConfig = {
      'accept': { icon: Check, title: 'Accepter', colorClass: 'text-green-600 hover:text-green-900 hover:bg-green-50' },
      'reject': { icon: X, title: 'Rejeter', colorClass: 'text-red-600 hover:text-red-900 hover:bg-red-50' },
      'start': { icon: Play, title: 'Commencer', colorClass: 'text-blue-600 hover:text-blue-900 hover:bg-blue-50' },
      'complete': { icon: CheckSquare, title: 'Terminer', colorClass: 'text-green-600 hover:text-green-900 hover:bg-green-50' }
    };

    return availableActions.map(action => ({
      action,
      ...actionConfig[action]
    }));
  };

  const getStatusBadge = (status) => {
    const statusInfo = taskService.getStatusInfo(status);
    return `px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.textColor}`;
  };

  const getStatusLabel = (status) => {
    const statusInfo = taskService.getStatusInfo(status);
    return statusInfo.label;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <>
      {/* Confirmation Modal */}
      {confirmationModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmer l'action</h3>
            <p className="text-gray-600 mb-4">{confirmationModal.message}</p>
            
            {/* Show comment form only for complete action */}
            {confirmationModal.action === 'complete' && (
              <div className="mb-6 space-y-4">
                <div>
                  <label htmlFor="completion-comment" className="block text-sm font-medium text-gray-700 mb-2">
                    Commentaire (optionnel)
                  </label>
                  <textarea
                    id="completion-comment"
                    value={completionComment}
                    onChange={(e) => setCompletionComment(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                    placeholder="Ajoutez un commentaire sur la complétion de cette tâche..."
                  />
                </div>
                
                {/* File upload section for completion */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pièces jointes (optionnel)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                      onChange={handleFileSelection}
                      className="hidden"
                      id="completion-file-input"
                    />
                    <label
                      htmlFor="completion-file-input"
                      className="cursor-pointer flex flex-col items-center justify-center text-gray-500 hover:text-gray-700"
                    >
                      <Upload className="h-8 w-8 mb-2" />
                      <span className="text-sm">Cliquez pour sélectionner des fichiers</span>
                      <span className="text-xs text-gray-400 mt-1">
                        PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG, GIF (max 5MB)
                      </span>
                    </label>
                  </div>
                  
                  {/* Selected files list */}
                  {selectedFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-sm font-medium text-gray-700">Fichiers sélectionnés:</p>
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <div className="flex items-center space-x-2">
                            <Paperclip className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-700">{file.name}</span>
                            <span className="text-xs text-gray-500">({taskService.formatFileSize(file.size)})</span>
                          </div>
                          <button
                            onClick={() => removeSelectedFile(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button 
                onClick={closeConfirmationModal} 
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button 
                onClick={handleConfirmAction} 
                disabled={isUploading}
                className="px-4 py-2 text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isUploading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                <span>{isUploading ? 'Traitement...' : 'Confirmer'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Upload Modal */}
      {attachmentModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Ajouter des pièces jointes
            </h3>
            <p className="text-gray-600 mb-4">
              Tâche: <span className="font-medium">{attachmentModal.taskName}</span>
            </p>
            
            {/* File upload area */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-4">
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                onChange={handleFileSelection}
                className="hidden"
                id="attachment-file-input"
              />
              <label
                htmlFor="attachment-file-input"
                className="cursor-pointer flex flex-col items-center justify-center text-gray-500 hover:text-gray-700"
              >
                <Upload className="h-12 w-12 mb-3" />
                <span className="text-sm font-medium">Cliquez pour sélectionner des fichiers</span>
                <span className="text-xs text-gray-400 mt-1 text-center">
                  PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG, GIF<br />
                  Taille maximale: 5MB par fichier
                </span>
              </label>
            </div>
            
            {/* Selected files list */}
            {selectedFiles.length > 0 && (
              <div className="mb-4 max-h-40 overflow-y-auto">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Fichiers sélectionnés ({selectedFiles.length}):
                </p>
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <Paperclip className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-700 truncate">{file.name}</span>
                        <span className="text-gray-500 flex-shrink-0">
                          ({taskService.formatFileSize(file.size)})
                        </span>
                      </div>
                      {uploadProgress[index] && (
                        <div className="flex items-center space-x-2 ml-2">
                          {uploadProgress[index] === 'uploading' && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                          )}
                          {uploadProgress[index] === 'success' && (
                            <Check className="h-4 w-4 text-green-600" />
                          )}
                          {uploadProgress[index] === 'error' && (
                            <X className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                      )}
                      {!uploadProgress[index] && (
                        <button
                          onClick={() => removeSelectedFile(index)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button 
                onClick={closeAttachmentModal} 
                disabled={isUploading}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button 
                onClick={handleAttachmentUpload} 
                disabled={selectedFiles.length === 0 || isUploading}
                className="px-4 py-2 text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isUploading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                <span>{isUploading ? 'Upload...' : 'Uploader'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Mes Tâches Assignées</h2>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button 
              onClick={fetchMyTasks} 
              className="mt-2 text-red-600 hover:text-red-800 underline"
            >
              Réessayer
            </button>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom de la Tâche
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Processus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date d'échéance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tasks.map((task) => {
                  const actionButtons = getActionButtons(task);
                  
                  return (
                    <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {task.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {task.process_details?.process_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(task.risk_action_details?.dueDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(task.status)}>
                          {getStatusLabel(task.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => handleViewDetails(task.id)} 
                            className="text-gray-600 hover:text-gray-900 p-1 hover:bg-gray-50 rounded transition-colors" 
                            title="Voir les détails"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          
                          {/* Upload attachment button for in_progress and completed tasks */}
                          {(task.status === 'in_progress' || task.status === 'completed') && (
                            <button 
                              onClick={() => openAttachmentModal(task.id, task.name)} 
                              className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-colors" 
                              title="Ajouter des pièces jointes"
                            >
                              <Upload className="h-4 w-4" />
                            </button>
                          )}
                          
                          {actionButtons.map((button) => {
                            const ActionIcon = button.icon;
                            return (
                              <button 
                                key={button.action}
                                onClick={() => handleTaskAction(task, button.action)} 
                                className={`${button.colorClass} p-1 rounded transition-colors`}
                                title={button.title}
                              >
                                <ActionIcon className="h-4 w-4" />
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.total_pages}
            totalItems={pagination.count}
            pageSize={pagination.page_size}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={loading}
            itemName="Tasks" 
          />
          {tasks.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-lg font-medium text-gray-500">
                Aucune tâche ne vous est assignée pour le moment.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}